import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column } from 'react-table';
import { Container, Col, Row } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import { useApiClient } from '../../hooks/useApiClient';
import TableContainer from '../../components/genericTable/TableContainer';
import { columnsDealer } from './columnsDealer';
import { buildDealersExportData } from './exportDealers';
import Restricted from '../../components/Restricted/Restricted';
import Btn from '../../components/Btn/Btn';
import {
	ExportFormat,
	FileExportMenu,
} from 'src/components/FileExportMenu/FileExportMenu';
import { downloadCSV, downloadXLSX } from 'src/utils';
import toaster from 'src/utils/toaster';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { GTMsendEvent } from '../../utils/trackingGA4';
import { DealerDTO, RoleEnum } from 'src/api';

const DealerListPage = () => {
	const { apiClient, handleApi } = useApiClient();
	const { t } = useTranslation();
	const { showError } = useErrorToast();

	const [response, setResponse]: [any, any] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const navigate = useNavigate();

	useEffect(() => {
		handleApi(apiClient.dealerController.getRegisterDealers())
			.then((res) => {
				setResponse(res);
				setLoading(false);
			})
			.catch((err) => {
				setError(err);
				setLoading(false);
			});
	}, []);

	const columns = useMemo(() => columnsDealer as Column<Object>[], []);
	const data = useMemo(() => response ?? [], [response]);

	const filteredRowsRef = useRef<DealerDTO[]>([]);
	const handleFilteredRowsChange = useCallback((rows: DealerDTO[]) => {
		filteredRowsRef.current = rows;
	}, []);

	const handleExportButton = (format: ExportFormat) => {
		try {
			const dealers = filteredRowsRef.current;

			if (!dealers.length) {
				toaster.error(i18next.t('no_data_available_for_download'));
				return;
			}

			const downloadData = buildDealersExportData(dealers);
			const fileName = i18next.t('dealer_list').replace(/ /g, '_');

			switch (format) {
				case 'xlsx':
					downloadXLSX(downloadData, fileName);
					break;
				case 'csv':
					downloadCSV(downloadData, fileName);
					break;
				default:
					console.warn(`[Horizon] Unsupported export format: ${format}`);
					break;
			}

			GTMsendEvent({
				event: 'CTA_download_file',
				evtCat: 'lista_dealer',
				evtAct: 'download',
				evtLab: `${fileName}.${format}`,
			});
		} catch (err) {
			showError(err, i18next.t('export_error'));
		}
	};

	const hanndleCreatenewDealer = () => {
		navigate('/manage-dealers/new');
		GTMsendEvent({
			event: 'CTA_create_new_dealer',
			evtCat: 'dealer',
			evtAct: 'create',
			evtLab: 'new_dealer',
		});
	};
	return (
		<Container fluid>
			<Row>
				<Col md={6} className="display-6">
					{t('dealer_list')}
				</Col>
				<Restricted to={[RoleEnum.BACKOFFICE, RoleEnum.MASTER, RoleEnum.ADMIN]}>
					<Col
						md={6}
						className="mt-4 mt-md-0 d-flex justify-content-md-end align-items-center gap-2"
					>
						<FileExportMenu
							isDisabled={loading || !data.length}
							allowedFormats={['csv', 'xlsx']}
							handleExport={handleExportButton}
						/>
						<Btn
							// to="/manage-dealers/new"
							onClick={hanndleCreatenewDealer}
						>
							<FaPlus />
							{t('create_new_dealer')}
						</Btn>
					</Col>
				</Restricted>
			</Row>

			<TableContainer
				dblClickCallback={(row: any) => navigate(`/manage-dealers/${row.id}`)}
				columns={columns}
				data={data}
				loading={loading}
				error={error}
				onFilteredRowsChange={handleFilteredRowsChange}
			></TableContainer>
		</Container>
	);
};

export default DealerListPage;
